import {
  feeRateService,
  type BasicFeePolicy,
  type BasicFeeType,
  type DetailFeeGroupRegisterRequest,
} from '@/entities/fee-rate/api/fee-rate.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const feeRateKeys = {
  all: ['fee-rates'] as const,
  basicHistoryLists: () => [...feeRateKeys.all, 'basic-history'] as const,
  basicPolicy: (settlementYearMonth: string) =>
    [...feeRateKeys.all, 'basic-policy', settlementYearMonth] as const,
  basicUserHistoryLists: () =>
    [...feeRateKeys.all, 'basic-user-history'] as const,
  detailGroupLists: () => [...feeRateKeys.all, 'detail-groups'] as const,
  detailGroupClinics: (userId: number) =>
    [...feeRateKeys.all, 'detail-clinics', userId] as const,
  hospitalDetail: (params: Record<string, unknown>) =>
    [...feeRateKeys.all, 'hospital-detail', params] as const,
  clinicDetail: (params: Record<string, unknown>) =>
    [...feeRateKeys.all, 'clinic-detail', params] as const,
};

export const useBasicFeeHistory = (
  search: {
    settlementYearMonth?: string;
    searchKeyword?: string;
    basicFeeType?: BasicFeeType;
  },
  pageable: Pageable,
) =>
  usePaginatedQuery({
    queryKeyPrefix: feeRateKeys.basicHistoryLists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      feeRateService.getBasicFeeHistory(s, p),
  });

export const useBasicFeePolicy = (settlementYearMonth: string) =>
  useQuery({
    queryKey: feeRateKeys.basicPolicy(settlementYearMonth),
    queryFn: () => feeRateService.getBasicFeePolicy(settlementYearMonth),
    enabled: Boolean(settlementYearMonth),
  });

export const useRegisterBasicFeePolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BasicFeePolicy) =>
      feeRateService.registerBasicFeePolicy(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: feeRateKeys.basicPolicy(data.settlementYearMonth),
      });
      queryClient.invalidateQueries({ queryKey: feeRateKeys.all });
    },
  });
};

export const useCopyBasicFeePolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      fromYearMonth,
      toYearMonth,
    }: {
      fromYearMonth: string;
      toYearMonth: string;
    }) => feeRateService.copyBasicFeePolicy(fromYearMonth, toYearMonth),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeRateKeys.all });
    },
  });
};

export const useBasicFeeUserHistory = (userId: number, pageable: Pageable) =>
  usePaginatedQuery({
    queryKeyPrefix: feeRateKeys.basicUserHistoryLists(),
    search: { userId },
    pageable,
    queryFn: async ({ pageable: p }) =>
      feeRateService.getBasicFeeUserHistory(userId, p),
    enabled: userId > 0,
  });

export const useDetailFeeGroups = (
  search: {
    searchKeyword?: string;
    basicFeeType?: BasicFeeType;
    detailFeeConfiguredOnly?: boolean;
    sort?: string;
  },
  pageable: Pageable,
) =>
  usePaginatedQuery({
    queryKeyPrefix: feeRateKeys.detailGroupLists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      feeRateService.getDetailFeeGroups(s, p),
  });

export const useDetailFeeGroupClinics = (userId: number) =>
  useQuery({
    queryKey: feeRateKeys.detailGroupClinics(userId),
    queryFn: () => feeRateService.getSettingTargetClinics(userId),
    enabled: userId > 0,
  });

export const useRegisterDetailFeeGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DetailFeeGroupRegisterRequest) =>
      feeRateService.registerDetailFeeGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeRateKeys.all });
    },
  });
};

export const useRemoveDetailFeeGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (detailGroupId: number) =>
      feeRateService.removeDetailFeeGroup(detailGroupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeRateKeys.all });
    },
  });
};

export const useHospitalDetailFeeGroup = (params: {
  userId: number;
  clinicId: number;
  basicFeeType: BasicFeeType;
}) =>
  useQuery({
    queryKey: feeRateKeys.hospitalDetail(params),
    queryFn: () => feeRateService.getHospitalDetailFeeGroup(params),
    enabled: params.userId > 0 && params.clinicId > 0,
  });

export const useClinicDetailFeeGroup = (params: {
  userId: number;
  clinicId: number;
  basicFeeType: BasicFeeType;
}) =>
  useQuery({
    queryKey: feeRateKeys.clinicDetail(params),
    queryFn: () => feeRateService.getClinicDetailFeeGroup(params),
    enabled: params.userId > 0 && params.clinicId > 0,
  });

export const useSaveHospitalDrugRules = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      feeRateService.saveHospitalDrugRules(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeRateKeys.all });
    },
  });
};

export const useSaveClinicDrugRates = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      feeRateService.saveClinicDrugRates(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeRateKeys.all });
    },
  });
};

export const useSaveClinicPharmaRates = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      feeRateService.saveClinicPharmaRates(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeRateKeys.all });
    },
  });
};
